import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { MdPersonAdd } from "react-icons/md";
import { useLocation, useNavigate } from "react-router";
import { useSelector } from "react-redux";
import { ADD_SUBACCOUNT_URL } from "../../api/apiRoutes";
import { State } from "../../store";
import { LOGIN_PAGE } from "../../routes";
import useFacility from "../../../utils/hooks/useFacility";
import useApiRequest from "../../../utils/hooks/useApiRequest";
import EATextField from "../shared/TextField/EATextField";
import EASelect from "../shared/EASelect/EASelect";
import EABirthDateInput from "../shared/BirthDateInput/EABirthDateInput";
import EAButton from "../shared/Buttons/EAButton";
import EAAlert from "../shared/EAAlert";
import SubaccountExplanation from "./SubaccountExplanation";
import EADivider from "../shared/EADivider";
import { mergeSearchWithTab } from "../../../utils/myAccountTabUrl";

interface FormInputs {
  firstName: string;
  lastName: string;
  sex: string;
  birthDate: Date | null;
  agreementNews: boolean;
}

interface IProps {
  onSuccess: () => void;
}

const SEX_OPTIONS = [
  { value: "M", label: "Mężczyzna" },
  { value: "K", label: "Kobieta" },
];

const validateBirthDate = (value: Date | null): string | true => {
  if (!value) return "Data urodzenia jest wymagana";
  if (isNaN(value.getTime())) return "Podaj prawidłową datę urodzenia";
  if (value > new Date()) return "Data nie może być z przyszłości";

  const minBirthDate = new Date();
  minBirthDate.setFullYear(minBirthDate.getFullYear() - 100);
  if (value < minBirthDate) return "Data urodzenia wskazuje na wiek powyżej 100 lat";

  return true;
};

const AddSubaccountForm: React.FC<IProps> = ({ onSuccess }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const facilityIds = useFacility();
  const { post } = useApiRequest();
  const userEmail = useSelector((state: State) => state.auth.userData?.email ?? "");

  const [isLoading, setIsLoading] = useState(false);
  const [apiErrors, setApiErrors] = useState<string[]>([]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      firstName: "",
      lastName: "",
      sex: "",
      birthDate: null,
      agreementNews: false,
    },
  });

  const onSubmit = async (data: FormInputs) => {
    setApiErrors([]);
    setIsLoading(true);

    const fullName = `${data.firstName} ${data.lastName}`;

    const body = {
      facilityIds,
      userMail: userEmail,
      subAccount: {
        objectId: 0,
        objectDb: 0,
        holderName: fullName,
        holderData1: fullName,
        sex: data.sex,
        birthDate: data.birthDate,
        agreementNews: data.agreementNews,
      },
    };

    try {
      const result = await post(ADD_SUBACCOUNT_URL, body);
      if (result?.isError) {
        setApiErrors(result.errorTable ?? ["Wystąpił błąd. Spróbuj ponownie."]);
      } else {
        onSuccess();
      }
    } catch {
      setApiErrors(["Wystąpił błąd. Spróbuj ponownie."]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="add-subaccount-page__layout">
      <form className="add-subaccount-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="add-subaccount-form__header">
          <h1>Dodaj dziecko do konta</h1>
          <p className="add-subaccount-form__subtitle">
            Uzupełnij dane dziecka, które będzie uczestniczyć w zajęciach. W przyszłości możesz dodać kolejne osoby do
            tego samego konta.{" "}
          </p>
        </div>

        <div className="add-subaccount-form__field">
          <EATextField
            label="Imię"
            name="firstName"
            fullWidth
            error={!!errors.firstName}
            helperText={errors.firstName?.message}
            register={register("firstName", { required: "Imię jest wymagane" })}
          />
        </div>
        <div className="add-subaccount-form__field">
          <EATextField
            label="Nazwisko"
            name="lastName"
            fullWidth
            error={!!errors.lastName}
            helperText={errors.lastName?.message}
            register={register("lastName", { required: "Nazwisko jest wymagane" })}
          />
        </div>

        <div className="add-subaccount-form__field">
          <Controller
            name="sex"
            control={control}
            rules={{ required: "Płeć jest wymagana" }}
            render={({ field }) => (
              <EASelect
                label="Płeć"
                name="sex"
                value={field.value}
                onChange={(e) => field.onChange(e.target.value)}
                options={SEX_OPTIONS}
                placeholder="Wybierz płeć"
                fullWidth
                error={!!errors.sex}
                helperText={errors.sex?.message}
              />
            )}
          />
        </div>

        <div className="add-subaccount-form__field">
          <Controller
            name="birthDate"
            control={control}
            rules={{ validate: validateBirthDate }}
            render={({ field, fieldState }) => (
              <EABirthDateInput
                label="Data urodzenia"
                name="birthDate"
                field={field}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
        </div>

        {/* <div className="add-subaccount-form__checkbox">
          <Controller
            name="agreementNews"
            control={control}
            render={({ field }) => (
              <EACheckbox
                name="agreementNews"
                checked={field.value}
                onChange={(checked) => field.onChange(checked)}
                label="Chcę otrzymywać powiadomienia email dotyczące zajęć tej osoby"
              />
            )}
          />
        </div> */}

        {apiErrors.length > 0 && (
          <div className="add-subaccount-form__error">
            <EAAlert type="error" messages={apiErrors} />
          </div>
        )}

        <div className="add-subaccount-form__field">
          <EADivider />
        </div>

        <div className="add-subaccount-form__buttons">
          <EAButton
            title="Wróć do Mojego Konta"
            variant="outlined"
            fullWidth
            onClick={() => navigate(`${LOGIN_PAGE}?${mergeSearchWithTab(location.search, "people")}`)}
          />
          <EAButton
            title="Dodaj osobę"
            variant="filled"
            inputType="submit"
            fullWidth
            endIcon={<MdPersonAdd />}
            loading={isLoading}
            loadingText="Dodawanie..."
          />
        </div>
      </form>

      <SubaccountExplanation />
    </div>
  );
};

export default AddSubaccountForm;
